import axios from 'axios';
import { UserInfo } from 'types/UserInfo';

/** A sign-in refused because the account is self-excluded, or (retryAfter,
 *  in seconds) because of too many wrong passwords. */
export interface LoginLock {
  permanent: boolean;
  lockedUntil?: string;
  retryAfter?: number;
}

let lastLoginLock: LoginLock | null = null;

/** The lock behind the last failed sign-in, read once. */
export function takeLoginLock(): LoginLock | null {
  const lock = lastLoginLock;
  lastLoginLock = null;
  return lock;
}

export const loginApi = async (
  username: string,
  password: string
): Promise<UserInfo | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    const response = await axios.post(`${baseUrl}/login`, {
      username,
      password,
    });
    return response.data;
  } catch (e: any) {
    const data = e?.response?.data;
    lastLoginLock =
      data?.code === 'account_locked'
        ? { permanent: !!data.permanent, lockedUntil: data.locked_until }
        : data?.code === 'too_many_attempts'
          ? { permanent: false, retryAfter: Number(data.retry_after) || 60 }
          : null;
    console.error(e);
    return null;
  }
};

export const logoutApi = async (loggedUser: UserInfo): Promise<boolean> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    await axios.post(
      `${baseUrl}/logout`,
      {
        refresh_token: loggedUser.refresh_token,
      },
      {
        headers: {
          Authorization: `Bearer ${loggedUser.access_token}`,
        },
      }
    );
    return true;
  } catch (e: any) {
    console.error(e);
    return false;
  }
};

export const changePassword = async (
  old_password: string,
  new_password: string,
  loggedUser: UserInfo
): Promise<any> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    await axios.post(
      `${baseUrl}/change_password`,
      {
        old_password,
        new_password,
        username: loggedUser.username,
      },
      {
        headers: {
          Authorization: `Bearer ${loggedUser.access_token}`,
        },
      }
    );
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const updateTheme = async (
  playerTheme: string,
  loggedUser: UserInfo
): Promise<boolean> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

  try {
    await axios.post(
      `${baseUrl}/update_theme`,
      { player_theme: playerTheme },
      {
        headers: {
          Authorization: `Bearer ${loggedUser.access_token}`,
        },
      }
    );
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export type TokenRefreshResult = {
  access: string;
  refresh: string;
};

export const accessTokenRefresh = async (
  loggedUser: UserInfo
): Promise<TokenRefreshResult | null> => {
  const baseUrl = process.env.REACT_APP_GAME_API_BACKEND_API ?? '';
  try {
    const response = await axios.post(
      `${baseUrl}/token/refresh`,
      {
        refresh: loggedUser.refresh_token,
      },
      {
        headers: {
          Authorization: `Bearer ${loggedUser.access_token}`,
        },
      }
    );
    const access = response.data.access as string | undefined;
    const refresh = response.data.refresh as string | undefined;
    if (!access || !refresh) return null;
    return { access, refresh };
  } catch (e) {
    console.error(e);
    return null;
  }
};
